/**
 * Pip — the single primitive for small circular indicators (score dots,
 * evasion pips, accent dots, side tags). Replaces three near-identical
 * inline-style snippets that used to live in overlay components.
 *
 * Two visual modes:
 *   - filled: solid disc in `color`
 *   - outline: hollow disc with a 1px `color` border
 *
 * Size defaults to `--pp-pip-size` (8px); pass `size` to override per
 * instance (e.g. larger accent dots in the metadata strip).
 */

import type { CSSProperties } from "react";

export interface PipProps {
  /** Color used for fill (when `filled`) or outline (otherwise). Accepts
   *  raw hex/rgb or a CSS var like `"var(--pp-accent-a)"`. */
  color: string;
  /** When true, render a solid disc. Otherwise render a 1px ring. */
  filled?: boolean;
  /** Override the pip diameter in px. Defaults to the `--pp-pip-size`
   *  token (8px). */
  size?: number;
  /** Optional soft glow (used by the metadata-strip accent dot). */
  glow?: boolean;
  /** ARIA hidden — pips are decorative; consumers wrap them in a labelled
   *  container. Default true. */
  ariaHidden?: boolean;
}

export function Pip({
  color,
  filled = false,
  size,
  glow = false,
  ariaHidden = true,
}: PipProps) {
  const style: CSSProperties = {
    display: "inline-block",
    width: size ?? "var(--pp-pip-size)",
    height: size ?? "var(--pp-pip-size)",
    borderRadius: "50%",
    background: filled ? color : "transparent",
    border: filled ? "none" : `1px solid ${color}`,
    flexShrink: 0,
    boxShadow: glow ? `0 0 8px ${color}` : undefined,
  };
  return <span style={style} aria-hidden={ariaHidden} />;
}
